"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { AudioPlayer } from "./AudioPlayer";
import { LinkPreview } from "./LinkPreview";
import { BodyLinkPreview } from "./BodyLinkPreview";
import { findFirstLinkInHtml, sameLinkTarget } from "@/lib/linkDetect";
import { PostHtml } from "./PostHtml";
import { FileChip } from "./FileChip";
import { VideoPlayer } from "./VideoPlayer";
import type { UploadStatus } from "@/lib/postRaw";
import {
  processCooked,
  extractOneboxes,
  extractYoutubeEmbeds,
  extractAndStripAudio,
  extractAndStripFiles,
  extractAndStripImages,
  isEmojiOnlyBody,
  cookedToPlainText,
} from "@/lib/processCooked";
import { cn } from "./ui/cn";
import type { OneboxRef, FileRef } from "@/lib/processCooked";

/**
 * Which surface the content is rendered on. Controls how much vertical room
 * the image gallery and YouTube embeds are allowed to take — a post can afford
 * a 340px gallery, a chat bubble cannot.
 */
type RichContentDensity = "post" | "comment" | "message";

type DensitySpec = {
  /** Margin above each block that hangs off the body. */
  gap: string;
  /** Max height of a lone image (rendered `object-contain`). */
  single: string;
  /** Fixed height of the 2-up grid. */
  pair: string;
  /** Fixed height of the 3-up / 4-up grid. */
  many: string;
  /** Gallery corner treatment — posts run edge-to-edge, bubbles are rounded. */
  frame: string;
  /** Width for YouTube embeds and video attachments. Must be a definite width, not a
   *  percentage: a chat bubble is shrink-to-fit, so `min(100%,…)` resolves
   *  against an indefinite width and collapses the embed to 0×0. */
  video: string;
  /**
   * Max height of a video attachment, in px — `single` again, as a NUMBER,
   * because `VideoPlayer` derives a width from it as well as a height
   * (OSO-665). Keep the two in step: a clip is not entitled to more vertical
   * room than a picture on the same surface, and before this it took nearly
   * twice as much.
   */
  videoHeight: number;
  /** How much body a `collapsible` surface shows before "Vis mere". */
  clamp: string;
  /**
   * The plain-text length below which the clamp is not applied at all — a cheap
   * floor, not the decision. `clamp` decides what is hidden and the measurement
   * further down decides whether anything is; this only spares every short body
   * a `-webkit-box` it could never fill. Erring high costs a middling body on a
   * narrow screen its fold, and nothing else.
   */
  foldFloorChars: number;
};

const DENSITY: Record<RichContentDensity, DensitySpec> = {
  post: {
    gap: "mt-3",
    single: "max-h-[480px]",
    pair: "h-[240px] sm:h-[300px]",
    many: "h-[280px] sm:h-[340px]",
    frame: "",
    video: "",
    videoHeight: 480,
    clamp: "line-clamp-12",
    foldFloorChars: 800,
  },
  comment: {
    gap: "mt-2",
    single: "max-h-[280px]",
    pair: "h-[150px] sm:h-[180px]",
    many: "h-[180px] sm:h-[220px]",
    frame: "rounded-lg overflow-hidden",
    video: "w-[360px] max-w-full",
    videoHeight: 280,
    clamp: "line-clamp-7",
    foldFloorChars: 450,
  },
  message: {
    gap: "mt-2",
    single: "max-h-[200px]",
    pair: "h-[110px] sm:h-[130px]",
    many: "h-[130px] sm:h-[160px]",
    frame: "rounded-lg overflow-hidden",
    video: "w-[320px] max-w-full",
    videoHeight: 200,
    // Never used — no DM surface passes `collapsible`. Present so the record
    // is exhaustive by type rather than by a caller remembering.
    clamp: "line-clamp-7",
    foldFloorChars: 450,
  },
};

interface RichContentProps {
  /** Discourse cooked HTML. */
  html: string | null | undefined;
  /** Server-provided gallery images (post/comment/message `image_urls`). */
  images?: string[];
  /** Optimised variants for the grid, if the server sent them. */
  gridImages?: string[];
  /** Video attachments (post/comment/message `attachments` of kind `video`).
   *  Rendered as players that never autoplay — see `VideoPlayer`.
   *
   *  `url` is `null` and `status` is not `ready` for a clip the worker is
   *  still encoding or could not encode (OSO-490); the player draws the
   *  placeholder or the error chip. Such a row reaches nobody but the author,
   *  so `onRemove` is set by the caller only when the viewer may edit — and
   *  `removeLabel` is the caller's word for what pressing it does, because on
   *  a row that is only this video it is a delete and not an edit. */
  videos?: Array<{
    url: string | null;
    /** The adaptive HLS source for a Bunny-hosted video (OSO-645); `null` for a
     *  local video, whose `url` is a directly playable MP4. */
    hlsUrl?: string | null;
    filename?: string;
    posterUrl?: string | null;
    /** The clip's pixel dimensions, so the player reserves its exact box
     *  instead of guessing a shape and jumping (OSO-665). Nullable on the wire
     *  — Bunny can answer without them — and `VideoPlayer` measures the
     *  element in that case. */
    width?: number | null;
    height?: number | null;
    status?: UploadStatus;
    onRemove?: () => void;
    removeLabel?: string;
    removeBusy?: boolean;
  }>;
  /**
   * The link whose preview card the author closed in the composer — OSO-568.
   *
   * The card below is DERIVED from the body, not stored in it, so this is the
   * only record that the author said no to it. Compared, never rendered.
   *
   * Optional because two surfaces have nothing to pass: a DM has no such field
   * (and its composer draws no preview to dismiss), and the legacy `oneboxes`
   * path below is markup inside a migrated Discourse body rather than a card
   * this app decided to draw — nothing can dismiss one, so nothing suppresses
   * one.
   */
  dismissedLinkPreviewUrl?: string | null;
  /** Controls gallery height + embed width. */
  density?: RichContentDensity;
  /**
   * Fold a long body down to a few lines with a "Vis mere" button (OSO-606).
   *
   * OPT-IN, because "long" is not a property of the text — it is a property of
   * the surface. A post on its own page is not too long: you opened it to read
   * it. The same post in a feed is a wall between you and the next one, which
   * is what the reporter photographed. So `PostCard` passes this on `feed` and
   * not on `detail`; comments pass it everywhere, since a long comment is long
   * on every screen it appears on.
   *
   * Only the WRITTEN BODY folds. Pictures, video, files and link cards stay
   * where they are — they are what the post is *about* as often as the text
   * is, and a photo hidden behind a "Vis mere" is a photo nobody sees.
   */
  collapsible?: boolean;
  /** Extra classes for the prose container. */
  className?: string;
  /** Called when a gallery image is clicked, with its index. When omitted, images are not clickable. */
  onImageClick?: (index: number) => void;
  /** Group this content is shown in — forwarded to the @mention hover-card so
   *  it's as contextual as the one already shown for post/comment authors. */
  categoryId?: number;
}

/**
 * The one renderer for a cooked Discourse body and everything that hangs off
 * it: audio players, video attachments, YouTube embeds, a bounded image
 * gallery and link-preview cards. Every surface that shows cooked HTML (posts, comments, DMs) should go
 * through this so a link in a DM looks like a link in a post.
 *
 * The lightbox is deliberately *not* owned here — callers already keep that
 * state (and the post lightbox carries reactions and a comment box). We only
 * report which index was clicked.
 */
export function RichContent({
  html,
  images,
  gridImages,
  videos,
  dismissedLinkPreviewUrl,
  density = "post",
  collapsible = false,
  className,
  onImageClick,
  categoryId,
}: RichContentProps) {
  const d = DENSITY[density];
  const [expanded, setExpanded] = useState(false);

  const { bodyHtml, videoIds, audioFiles, files, strippedImages, oneboxes, bareLink } =
    useMemo(() => {
    if (!html) {
      return {
        bodyHtml: "",
        videoIds: [] as string[],
        audioFiles: [] as Array<{ src: string; filename: string }>,
        files: [] as FileRef[],
        strippedImages: [] as string[],
        oneboxes: [] as OneboxRef[],
        bareLink: null as string | null,
      };
    }
    // Oneboxes must be read off the *raw* HTML: processCooked deletes the
    // resolved card, and the title/description/thumbnail/favicon Discourse
    // already fetched only exist inside it. Seeding LinkPreview with them keeps
    // the card identical to what Discourse rendered, with no /api/og round-trip.
    const oneboxes = extractOneboxes(html);
    // Order matters. Audio first (attachment anchors would otherwise survive
    // into the body), then the remaining (non-audio) attachment anchors —
    // otherwise they'd render as bare links — then processCooked — its
    // YouTube-onebox rule matches on the thumbnail <img>, so images must not
    // be stripped before it — then the embeds, then the images.
    const { html: noAudio, audioFiles } = extractAndStripAudio(html);
    const { html: noFiles, files } = extractAndStripFiles(noAudio);
    const { html: noVideo, videoIds } = extractYoutubeEmbeds(processCooked(noFiles));
    const { html: bodyHtml, images: strippedImages } = extractAndStripImages(noVideo);
    // The port's renderer emits plain anchors, so a post with a link has no
    // onebox to extract. Fall back to the first anchor — but only when there
    // is no onebox, so migrated posts that still carry one are unaffected and
    // never show two cards for the same link.
    const bareLink = oneboxes.length === 0 ? findFirstLinkInHtml(bodyHtml) : null;
    return { bodyHtml, videoIds, audioFiles, files, strippedImages, oneboxes, bareLink };
  }, [html]);

  /**
   * …UNLESS THE AUTHOR ALREADY SAID NO TO IT (OSO-568).
   *
   * Outside the memo above because it is not a fact about the html: the same
   * body renders with or without the card depending on what its author sent.
   * `sameLinkTarget` rather than `===` because the composer and the rendered
   * `href` spell the same url differently — see its header for the two ways.
   */
  const previewLink = sameLinkTarget(bareLink, dismissedLinkPreviewUrl) ? null : bareLink;

  // The server strips uploads out of `body_html` and sends them as `image_urls`
  // instead, so on that surface `strippedImages` comes back empty and the prop
  // supplies the gallery. On surfaces whose server leaves the images in the
  // cooked HTML (DMs) the stripped ones are the gallery. The prop wins when it
  // has anything, so a body that still contains its uploads never shows them twice.
  const gallery = images && images.length > 0 ? images : strippedImages;
  // Optimised variants only line up with the prop-supplied list.
  const grid =
    images && images.length > 0 && gridImages && gridImages.length > 0
      ? gridImages
      : gallery;

  const count = gallery.length;
  const hasBody = bodyHtml.trim().length > 0;

  /**
   * IS ANYTHING ACTUALLY HIDDEN BEHIND "VIS MERE"?
   *
   * The browser is asked, not the character count. Until OSO-668 the count was
   * the whole decision, and it restated the clamp in characters at an assumed
   * ~70 to a line — but a wide desktop column holds closer to 100. A
   * 460-character comment cleared the threshold, `line-clamp-7` cut nothing,
   * and the reader got a button that revealed text already on the screen.
   *
   * `scrollHeight > clientHeight` is that question answered at the width the
   * reader is actually at, and the observer keeps the answer true as fonts, a
   * rotation or a late card change the height. The character count survives
   * only as a floor (`foldFloorChars`): under it we do not clamp, so we do not
   * measure either.
   */
  const bodyRef = useRef<HTMLDivElement>(null);
  const [bodyOverflows, setBodyOverflows] = useState(false);
  const clampable =
    collapsible && hasBody && cookedToPlainText(bodyHtml).length > d.foldFloorChars;
  const folded = clampable && !expanded;

  useEffect(() => {
    const el = bodyRef.current;
    // Expanded, the clamp is off and nothing is clipped, so measuring would
    // answer "no" and take away the "Vis mindre" that got the reader here.
    if (!folded || !el) return;
    // The +1 is sub-pixel rounding: a clamped box can report a scrollHeight a
    // fraction taller than itself with nothing at all behind the fold.
    const measure = () => setBodyOverflows(el.scrollHeight > el.clientHeight + 1);
    // The first measurement is the whole point: the answer does not exist
    // until the browser has laid the clamped body out.
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [folded, bodyHtml]);

  // `clampable` as well, so a body edited down to nothing cannot leave a
  // button behind while the effect is early-returning on an expanded reader.
  const canToggle = clampable && bodyOverflows;

  /**
   * EMOJI SIZING BELONGS HERE, NOT IN EACH CALLER'S CLASS STRING.
   *
   * It was in three of them, at three values — 1.2em in a DM, `h-5 w-5` in a
   * comment, and in a post no size at all, so a custom emoji rendered at the
   * upload's own pixel size (OSO-450). `.theta-emoji-scale` in `globals.css`
   * is the single rule; this is the one renderer every cooked body goes
   * through, so attaching it here reaches posts, comments and DMs at once.
   *
   * `theta-emoji-only` is the double-size case, and "only" is taken
   * literally: nothing but emoji in the body AND nothing hanging off it, so a
   * photo with an emoji caption stays ordinary size.
   */
  const emojiOnly =
    isEmojiOnlyBody(bodyHtml) &&
    count === 0 &&
    oneboxes.length === 0 &&
    !previewLink &&
    videoIds.length === 0 &&
    audioFiles.length === 0 &&
    files.length === 0 &&
    (videos?.length ?? 0) === 0;

  return (
    <>
      {hasBody && (
        <PostHtml
          html={bodyHtml}
          className={cn(
            className,
            "theta-emoji-scale",
            emojiOnly && "theta-emoji-only",
            folded && d.clamp,
          )}
          categoryId={categoryId}
          bodyRef={bodyRef}
        />
      )}

      {canToggle && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          className="text-sm font-medium text-primary hover:underline mt-1"
        >
          {expanded ? "Vis mindre" : "Vis mere"}
        </button>
      )}

      {oneboxes.map((onebox) => (
        <div key={onebox.url} className={d.gap}>
          <LinkPreview url={onebox.url} seed={onebox} />
        </div>
      ))}

      {previewLink && <BodyLinkPreview url={previewLink} gapClass={d.gap} />}

      {videoIds.map((id) => (
        <div key={id} className={`${d.gap} ${d.video}`.trim()}>
          <div className="youtube-embed">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${id}`}
              title="YouTube-video"
              /* No `autoplay` in the allow-list: an embed must not be able to
                 start itself, whatever its src says (OSO-437). */
              allow="fullscreen; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        </div>
      ))}

      {audioFiles.map((af, i) => (
        <div key={`${af.src}-${i}`} className={d.gap}>
          <AudioPlayer src={af.src} filename={af.filename} />
        </div>
      ))}

      {(videos ?? []).map((v, i) => (
        // KEYED BY POSITION, not by url: a clip that is still encoding has NO
        // url, so two of them on one post would collide on `null-…` and React
        // would reuse one player's state for the other (OSO-490).
        <div key={`video-${i}`} className={`${d.gap} ${d.video}`.trim()}>
          <VideoPlayer
            src={v.url}
            hlsUrl={v.hlsUrl ?? undefined}
            filename={v.filename}
            poster={v.posterUrl ?? undefined}
            width={v.width}
            height={v.height}
            maxHeight={d.videoHeight}
            status={v.status}
            onRemove={v.onRemove}
            removeLabel={v.removeLabel}
            removeBusy={v.removeBusy}
          />
        </div>
      ))}

      {files.map((f, i) => (
        <div key={`${f.href}-${i}`} className={d.gap}>
          <FileChip name={f.filename} meta={fileMeta(f)} href={f.href} />
        </div>
      ))}

      {/* Facebook-style grid with a *fixed* height on the multi-image layouts:
          without one the cells grow to the images' natural aspect ratio and a
          couple of portrait phone screenshots turn the gallery into a wall
          taller than the post itself (OSO-324). */}
      {count > 0 && (
        <div
          data-testid="rich-content-gallery"
          className={`${d.gap} grid gap-0.5 ${d.frame} ${
            count === 1
              ? "grid-cols-1"
              : count === 2
                ? `grid-cols-2 ${d.pair}`
                : `grid-cols-2 grid-rows-2 ${d.many}`
          }`.replace(/\s+/g, " ").trim()}
        >
          {grid.slice(0, 4).map((url, i) => {
            const isSingle = count === 1;
            const spanClass = count === 3 && i === 0 ? "row-span-2" : "";
            const showOverlay = count > 4 && i === 3;
            const cellClass = `relative bg-muted overflow-hidden ${spanClass}${
              onImageClick ? " cursor-pointer" : ""
            }${isSingle ? " flex items-center justify-center" : " min-h-0"}`;

            const picture = (
              <>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={url}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className={
                    isSingle ? `max-w-full ${d.single} block` : "w-full h-full object-cover"
                  }
                />
                {showOverlay && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white text-2xl font-bold">+{count - 4}</span>
                  </div>
                )}
              </>
            );

            return onImageClick ? (
              <button
                key={i}
                type="button"
                onClick={() => onImageClick(i)}
                aria-label={`Vis billede ${i + 1}`}
                className={cellClass}
              >
                {picture}
              </button>
            ) : (
              <div key={i} className={cellClass}>
                {picture}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/** "PDF · 149 KB", or just whichever half is available. */
function fileMeta(f: FileRef): string | undefined {
  const parts = [f.ext && f.ext.toUpperCase(), f.size].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}
